// src/screens/StreamingAccountsScreen.js
// ============================================================================
// STREAMING HUB - CritCash Stream Key
//
// Multi-stream/restreaming REMOVED (founder, 2026-07-07): pro streamers use
// off-site tools (Restream.io etc.); the Mux simulcast backend was stripped
// the same day.
// SUPERSEDED 2026-08-03: restream is now a paid-member product (Simulcast
// section below) — see RESTREAM_PRODUCT_PLAN.md. VM-based fan-out; the Mux
// simulcast backend stays dead.
// DESKTOP MODE 2026-10-21 (CritCast desktop app v1, design S8.2 W3): inside
// the app (isDesktopApp()) the Server URL / Stream Key block is replaced by
// one line, because CritCast writes the key into OBS itself; Regenerate stays
// and tells the app the key changed. A "Clip that (30 s)" button shows under
// the health line while live (S5.8 item 3).
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, Alert, ScrollView,
  ActivityIndicator, Platform, Linking, TextInput, Image
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import * as Clipboard from 'expo-clipboard';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import { useError } from '../components/ErrorContext';
import { db } from '../../firebaseConfig';
import Header from '../components/Header';
import NavBar from '../components/NavBar';
import GameTilePicker from '../components/GameTilePicker';
import StreamScheduleEditor from '../components/StreamScheduleEditor'; // evergreen Step 1.2
import SquadManagePanel, { useTeamStreamsVisible, SquadLinkPrompt } from '../components/SquadManagePanel';
import useMembershipsLive from '../hooks/useMembershipsLive';
import { isDesktopApp } from '../utils/platform';

// Dev-only demo mode (?demo=1 on localhost): renders the screen with mock
// stream-key data for how-to / marketing screenshots — no auth, no callables.
// Inert in production (hostname check).
const isDemoMode = () =>
  Platform.OS === 'web' &&
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1'].includes(window.location.hostname) &&
  new URLSearchParams(window.location.search).get('demo') === '1';

// Client mirror of helpers.isAgeVerifiedIdentity (functions/modules/helpers.js):
// the ONE earn gate is a verified government ID with age confirmed 18+.
// Display only (the "Verify to start earning" banner) — the server decides
// who receives Super Crits from the users doc, never from this.
const isAgeVerifiedIdentity = (userData) => {
  const g = userData?.verification?.governmentId;
  return !!g && g.status === 'verified' && g.ageVerified === true;
};

// Stream health severity (2026-08-03). A vertical canvas breaks match
// verification outright; low resolution only degrades it — so red vs amber.
const streamHealthTone = (issues) => {
  if (!issues || issues.length === 0) return '#2ECC71';
  return issues.includes('vertical') ? '#E74C3C' : '#FCD000';
};

// ============================================================================
// HUB DESKTOP MODE + CLIP THAT HELPERS (CritCast desktop app v1, design
// S8.2 W3, S5.8 item 3, S8.1 clip presence row, S3.10 Hub regenerate row).
// Plain strings and functions only (no imports, no JSX): the offline harness
// web-landing/scripts/test-streaming-hub-desktop.mjs evaluates this block as
// written. Every string here is member-facing copy: ASCII, money-free, no
// em dash, product name CritCast.
// BEGIN W3 HUB HELPERS
const HUB_DESKTOP_KEY_LINE = 'CritCast writes your stream key into OBS for you.';
// The section description in the app (the browser one names OBS, Streamlabs
// and any RTMP software, which the app replaces).
const HUB_DESKTOP_DESCRIPTION = "CritCast goes live with this key through OBS. One key works for every game you play, and it's also how you take part in events.";
// A live canvas issue in the app: the server's line tells a browser member to
// edit OBS by hand, which the app reads as drift, so the app gets the
// Broadcast panel's fix instead (P-180 "Apply and restart OBS", refused while
// live).
const HUB_DESKTOP_HEALTH_LINE = 'Your stream needs a widescreen 16:9 canvas at 720p or higher for verified games. Stop the stream, then click Apply and restart OBS in the Broadcast panel.';

// The ask is fixed at 30 s in v1 (founder call 3 default; 15..60 allowed by
// the server). The SDK default timeout is 70 s and the server deadline 60 s.
const CLIP_REQUEST_SECONDS = 30;
const CLIP_CALL_TIMEOUT_MS = 75000;
// One clip every 20 seconds after a success (server rate limit), a 5 s local
// back-off after any refusal or failure (the same back-off the panel uses;
// the server restores lastAt on its "stream just started" refusal), and the
// success line stays up for 6 s.
const CLIP_SUCCESS_COOLDOWN_MS = 20000;
const CLIP_REFUSAL_BACKOFF_MS = 5000;
const CLIP_TOAST_MS = 6000;

// Control labels (P-142): they describe the ask, so the 30 stays.
const CLIP_LABEL = 'Clip that (30 s)';
const CLIP_BUSY_LABEL = 'Clipping...';
const CLIP_COOLDOWN_TEMPLATE = 'Clip that ({n})';
const CLIP_OPEN_MY_VIDEOS = 'Open My Videos';

// Result lines (P-143): {n} = seconds actually saved, {w} = the window the
// playlist offered. No digit literal: the length is a runtime value.
const CLIP_SAVED_TEMPLATE = 'Saved a {n}-second clip to My Videos.';
const CLIP_SAVED_TRIMMED_TEMPLATE = 'Saved the last {n} seconds to My Videos.';
const CLIP_WINDOW_TEMPLATE = 'Your stream window is {w} seconds right now.';
const CLIP_SAVED_PLAIN = 'Saved a clip to My Videos.';

// P-143a: a resource-exhausted that is not one of the server's two rate-limit
// sentences word for word is a refusal before the handler ran.
const CLIP_BUSY_LINE = 'Clips are busy right now. Try again in a moment.';
const CLIP_SERVER_COOLDOWN_LINE = 'One clip every 20 seconds. Try again in a moment.';
const CLIP_SERVER_DAILY_LINE = 'You have saved 30 clips today. The limit resets at midnight Central.';
// S5.3 row 11, word for word. The Hub also shows it when the web SDK reports
// a bare "internal" with no server sentence: in a browser that is a call the
// handler never answered (a dropped connection, or a refusal before the
// handler ran, such as a Cloud Run 429, which carries no CORS headers), so
// no clip was saved.
const CLIP_SERVER_FAILED_LINE = 'Could not save the clip. Try again.';
// Only for the SDK's own 75 s client deadline: the request reached the
// server and the clip may still have been saved, so the line says that.
const CLIP_UNCONFIRMED_LINE = 'Could not confirm the clip. Check My Videos, then try again.';
// Sentences the web SDK writes itself (never the server's member copy).
const CLIP_SDK_SENTENCES = [
  'Response is not valid JSON object.',
  'Response is missing data field.',
  'Firebase Functions instance was deleted.',
  'Request was cancelled.',
  'Request was cancelled',
];

// The S8.1 presence probe: clipMyStream({ seconds: 0 }) is refused with
// invalid-argument (S5.3 row 2) before any write when the callable is there.
const CLIP_PROBE_SECONDS = 0;

// Page-session memory (S8.1): missing = clipMyStream is not deployed, and the
// button stays hidden until the page reloads; probe = the one presence probe
// of this page session (a promise once started).
const hubClipSession = { missing: false, probe: null };

function hideKeyBlock(isDesktop) {
  return isDesktop === true;
}

// The stream health line, or null when there is nothing to show (offline,
// an unreadable probe, no message). In the app a line with issues is
// HUB_DESKTOP_HEALTH_LINE; a healthy line is the server's in both.
function hubHealthLine(health, isDesktop) {
  // The same truthy test the screen used before W3, so the browser view is unchanged.
  if (!health || !health.ok || !health.live || !health.message) return null;
  const hasIssues = Array.isArray(health.issues) && health.issues.length > 0;
  if (isDesktop === true && hasIssues) return HUB_DESKTOP_HEALTH_LINE;
  return health.message;
}

function showClipButton({ keyStatus, healthLive, clipAvailable } = {}) {
  if (clipAvailable === false) return false;
  return keyStatus === 'live' || healthLive === true;
}

// "storage-full: Your storage is full..." -> "Your storage is full..."
// (the same rule as MyVideosScreen's upload error).
function stripToken(message) {
  return String(message == null ? '' : message).replace(/^[a-z-]+:\s*/i, '');
}

function fillClipTemplate(template, n, w) {
  return template.split('{n}').join(String(n)).split('{w}').join(String(w));
}

function clipWholeSeconds(value) {
  const n = Math.floor(Number(value));
  return Number.isFinite(n) && n >= 1 ? n : null;
}

// The line after a clip. Takes the callable's success data
// ({ seconds, trimmed, windowSeconds, ... }), or a panel-style
// { ok: false, message } failure.
function clipToast(result) {
  const r = result || {};
  if (r.ok === false) return stripToken(r.message).trim() || CLIP_SERVER_FAILED_LINE;
  const n = clipWholeSeconds(r.seconds);
  if (n === null) return CLIP_SAVED_PLAIN;
  if (r.trimmed === true) {
    const first = fillClipTemplate(CLIP_SAVED_TRIMMED_TEMPLATE, n);
    const w = clipWholeSeconds(r.windowSeconds);
    return w === null ? first : first + ' ' + fillClipTemplate(CLIP_WINDOW_TEMPLATE, n, w);
  }
  return fillClipTemplate(CLIP_SAVED_TEMPLATE, n);
}

// A thrown callable error as { code, raw, sentence }: code without the
// "functions/" prefix; sentence = false when the message is empty, only a
// code ("internal", "resource-exhausted [429]", "NOT_FOUND") or one of the
// SDK's own sentences, so the server said nothing a member should read.
function clipErrorParts(error) {
  const code = String((error && error.code) || '').replace(/^functions\//, '');
  const raw = String((error && error.message) || '').trim();
  const text = stripToken(raw).trim();
  const sentence = !!text
    && !/^[a-z]+([-_][a-z]+)*( \[\d+\])?$/i.test(text)
    && CLIP_SDK_SENTENCES.indexOf(raw) === -1;
  return { code, raw, text, sentence };
}

// Maps a thrown callable error from a press to { missing, message }.
// missing = the function is not deployed (hide the button for the page
// session, no line).
function clipFailure(error) {
  const { code, raw, text, sentence } = clipErrorParts(error);
  if (code === 'not-found') return { missing: true, message: null };
  if (code === 'resource-exhausted'
    && raw !== CLIP_SERVER_COOLDOWN_LINE && raw !== CLIP_SERVER_DAILY_LINE) {
    return { missing: false, message: CLIP_BUSY_LINE };
  }
  if (sentence) return { missing: false, message: text };
  if (code === 'deadline-exceeded') return { missing: false, message: CLIP_UNCONFIRMED_LINE };
  return { missing: false, message: CLIP_SERVER_FAILED_LINE };
}

// The S8.1 presence verdict from the probe's error: true = clipMyStream is
// absent. not-found is what a client that skips CORS sees (main's Node
// client, the emulator); a browser sees a bare "internal" instead, because
// the preflight to a function that is not deployed gets a 404 with no CORS
// headers, fetch throws, and the SDK reports status 0 as internal/internal.
// invalid-argument (present) and any other answer leave the button shown.
function clipProbeMissing(error) {
  const { code, sentence } = clipErrorParts(error);
  if (code === 'not-found') return true;
  return code === 'internal' && !sentence;
}

// Runs the presence probe once per page session (call = the clipMyStream
// callable) and resolves true when the button may show. Every later caller,
// a remount included, shares the first probe's answer.
function probeClipPresence(call, session) {
  const s = session || hubClipSession;
  if (!s.probe) {
    s.probe = Promise.resolve()
      .then(() => call({ seconds: CLIP_PROBE_SECONDS }))
      .then(() => !s.missing, (error) => {
        if (clipProbeMissing(error)) s.missing = true;
        return !s.missing;
      });
  }
  return s.probe;
}

function clipCooldownSeconds(untilMs, nowMs) {
  if (!Number.isFinite(untilMs) || !Number.isFinite(nowMs)) return 0;
  const left = untilMs - nowMs;
  return left > 0 ? Math.ceil(left / 1000) : 0;
}

function clipButtonLabel({ busy, cooldownUntilMs, nowMs } = {}) {
  if (busy) return CLIP_BUSY_LABEL;
  const s = clipCooldownSeconds(cooldownUntilMs, nowMs);
  return s > 0 ? fillClipTemplate(CLIP_COOLDOWN_TEMPLATE, s) : CLIP_LABEL;
}

function clipButtonDisabled({ busy, cooldownUntilMs, nowMs } = {}) {
  return !!busy || clipCooldownSeconds(cooldownUntilMs, nowMs) > 0;
}

// Desktop app only: tell CritCast the key changed so it re-fetches the key
// and rewrites OBS when not live (S3.10). The signal carries no payload, so
// the key never crosses the bridge. An older app build without the method,
// or a refused call, changes nothing on the page.
function notifyDesktopKeyRotated(win) {
  try {
    const bridge = win && win.critcashDesktop;
    if (!bridge || bridge.isDesktopApp !== true) return false;
    const stream = bridge.stream;
    if (!stream || typeof stream.keyRotated !== 'function') return false;
    const pending = stream.keyRotated();
    if (pending && typeof pending.catch === 'function') pending.catch(() => {});
    return true;
  } catch (e) {
    return false;
  }
}
// END W3 HUB HELPERS

// BEGIN W3 HUB CLIP BUTTON
// "Clip that (30 s)" on the Streaming Hub. Saves the last 30 seconds of the
// member's own live stream to My Videos through the same clipMyStream the
// desktop panel uses. The parent renders it only while live.

// The one clipMyStream callable (S5.8 item 3): the press and the S8.1
// presence probe both go through it.
const clipMyStreamCallable = () => httpsCallable(getFunctions(), 'clipMyStream', { timeout: CLIP_CALL_TIMEOUT_MS });

const HubClipButton = ({ navigation, onMissing }) => {
  const [busy, setBusy] = useState(false);
  const [cooldownUntilMs, setCooldownUntilMs] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [note, setNote] = useState(null); // { ok, text }
  const mounted = useRef(true);

  // Set back to true on every mount: StrictMode and Fast Refresh run the
  // cleanup and then the effect again on the same ref.
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Tick the countdown label until the cooldown ends.
  useEffect(() => {
    if (!cooldownUntilMs) return undefined;
    const id = setInterval(() => {
      const t = Date.now();
      setNowMs(t);
      if (t >= cooldownUntilMs) {
        clearInterval(id);
        setCooldownUntilMs(null);
      }
    }, 250);
    return () => clearInterval(id);
  }, [cooldownUntilMs]);

  // The success line stays up for CLIP_TOAST_MS; a refusal stays until the
  // next press.
  useEffect(() => {
    if (!note || !note.ok) return undefined;
    const id = setTimeout(() => setNote(null), CLIP_TOAST_MS);
    return () => clearTimeout(id);
  }, [note]);

  const disabled = clipButtonDisabled({ busy, cooldownUntilMs, nowMs });
  const label = clipButtonLabel({ busy, cooldownUntilMs, nowMs });

  const handleClip = async () => {
    if (disabled) return;
    setBusy(true);
    setNote(null);
    try {
      const clipMyStream = clipMyStreamCallable();
      const result = await clipMyStream({ seconds: CLIP_REQUEST_SECONDS, via: 'web' });
      if (!mounted.current) return;
      const at = Date.now();
      setNote({ ok: true, text: clipToast(result && result.data) });
      setNowMs(at);
      setCooldownUntilMs(at + CLIP_SUCCESS_COOLDOWN_MS);
    } catch (error) {
      if (!mounted.current) return;
      const failure = clipFailure(error);
      if (failure.missing) {
        if (onMissing) onMissing();
        return;
      }
      const at = Date.now();
      setNote({ ok: false, text: failure.message });
      setNowMs(at);
      setCooldownUntilMs(at + CLIP_REFUSAL_BACKOFF_MS);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <View style={styles.clipBox}>
      <TouchableOpacity
        style={[styles.clipButton, disabled ? styles.clipButtonDisabled : null]}
        onPress={handleClip}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={{ disabled, busy }}
      >
        <Text style={[styles.clipButtonText, disabled ? styles.clipButtonTextDisabled : null]}>{label}</Text>
      </TouchableOpacity>
      {note ? (
        <View style={[styles.clipNote, note.ok ? styles.clipNoteOk : styles.clipNoteRefused]} accessibilityLiveRegion="polite">
          <Text style={[styles.clipNoteText, note.ok ? styles.clipNoteTextOk : styles.clipNoteTextRefused]}>{note.text}</Text>
          {note.ok ? (
            <TouchableOpacity
              onPress={() => { if (navigation) navigation.navigate('MyVideos'); }}
              accessibilityRole="link"
            >
              <Text style={styles.clipNoteLink}>{CLIP_OPEN_MY_VIDEOS}</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}
    </View>
  );
};
// END W3 HUB CLIP BUTTON

// ============================================================================
// CRITCASH STREAM KEY SECTION
// ============================================================================
const CritCashStreamSection = ({ showError, navigation }) => {
  const [keyData, setKeyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showFullKey, setShowFullKey] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  // Stream health: what the ingest is ACTUALLY carrying. Match results are
  // verified from the member's video, so a 640x360 or vertical canvas quietly
  // wrecks that read. Warn, never block — and stay silent when offline.
  const [health, setHealth] = useState(null);
  // W3: true once this page session learned clipMyStream is not deployed
  // (the presence probe, or a press that got not-found; S8.1).
  const [clipMissing, setClipMissing] = useState(hubClipSession.missing);

  const functions = getFunctions();
  // W3: inside the CritCast desktop app the key is written into OBS by the
  // app, so the Server URL / Stream Key block is not shown (S1.7, S8.2 W3).
  const inDesktopApp = isDesktopApp();
  const markClipMissing = () => {
    hubClipSession.missing = true;
    setClipMissing(true);
  };

  useEffect(() => {
    fetchStreamKey();
  }, []);

  // W3 Clip that gate (S5.8 item 3), and the S8.1 presence probe the first
  // time it opens in this page session: clipMyStream({ seconds: 0 }) answers
  // invalid-argument when the callable is deployed; probeClipPresence reads
  // not-found or a browser's bare "internal" as absent and hides the button.
  const clipGateOpen = !!keyData?.hasKey && !isDemoMode() && showClipButton({
    keyStatus: keyData?.status,
    healthLive: health?.live,
    clipAvailable: !clipMissing,
  });
  useEffect(() => {
    if (!clipGateOpen) return undefined;
    let alive = true;
    probeClipPresence(clipMyStreamCallable()).then((present) => {
      if (alive && !present) setClipMissing(true);
    });
    return () => { alive = false; };
  }, [clipGateOpen]);

  const fetchStreamKey = async () => {
    setIsLoading(true);
    if (isDemoMode()) {
      setKeyData({
        hasKey: true,
        streamKey: 'ch_critseeker_7f2a?key=sk_live_Kt9mQx4Zr8Wv2Nb6',
        rtmpUrl: 'rtmp://stream.critcash.com/live',
        hlsUrl: 'https://play.critcash.com/live/ch_critseeker_7f2a/index.m3u8',
        status: 'active',
      });
      setHealth({
        ok: true,
        live: true,
        width: 1920,
        height: 1080,
        issues: [],
        message: 'Stream looks good for match verification (1920x1080).',
      });
      setIsLoading(false);
      return;
    }
    try {
      const getMyStreamKey = httpsCallable(functions, 'getMyStreamKey');
      const result = await getMyStreamKey({});
      setKeyData(result.data);
      if (result.data?.hasKey) fetchStreamHealth();
    } catch (error) {
      console.error('Error fetching stream key:', error);
      setKeyData({ hasKey: false });
    } finally {
      setIsLoading(false);
    }
  };

  // Deliberately silent on failure: an unreachable probe must never make a
  // working stream look broken. The backend already reports unknown (not bad)
  // when it can't read the ingest.
  const fetchStreamHealth = async () => {
    try {
      const getMyStreamHealth = httpsCallable(functions, 'getMyStreamHealth');
      const result = await getMyStreamHealth({});
      setHealth(result.data);
    } catch (error) {
      setHealth(null);
    }
  };

  const handleGenerateKey = async () => {
    setIsGenerating(true);
    try {
      const generateStreamKey = httpsCallable(functions, 'generateStreamKey');
      const result = await generateStreamKey({});
      
      setKeyData({
        hasKey: true,
        streamKey: result.data.streamKey,
        rtmpUrl: result.data.rtmpUrl,
        hlsUrl: result.data.hlsUrl,
        createdAt: result.data.createdAt,
        status: 'active'
      });
      
      showError('Success', 'Your stream key has been generated!');
    } catch (error) {
      showError('Error', error.message || 'Failed to generate stream key.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateKey = async () => {
    const confirmRegenerate = Platform.OS === 'web' 
      ? window.confirm('Regenerate Stream Key?\n\nThis will revoke your current key immediately. Any active stream will be disconnected.')
      : await new Promise((resolve) => {
          Alert.alert(
            'Regenerate Stream Key?',
            'This will revoke your current key immediately. Any active stream will be disconnected.',
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
              { text: 'Regenerate', style: 'destructive', onPress: () => resolve(true) },
            ]
          );
        });

    if (!confirmRegenerate) return;

    setIsRegenerating(true);
    try {
      const regenerateStreamKey = httpsCallable(functions, 'regenerateStreamKey');
      const result = await regenerateStreamKey({});
      
      setKeyData({
        hasKey: true,
        streamKey: result.data.streamKey,
        rtmpUrl: result.data.rtmpUrl,
        hlsUrl: result.data.hlsUrl,
        createdAt: result.data.createdAt,
        status: 'active'
      });
      // W3: in the desktop app, tell CritCast the key changed so it re-fetches
      // it and rewrites OBS (S3.10). No payload: the key never crosses.
      if (isDesktopApp()) notifyDesktopKeyRotated(typeof window !== 'undefined' ? window : null);

      setShowFullKey(false);
      showError('Success', 'New stream key generated!');
    } catch (error) {
      showError('Error', error.message || 'Failed to regenerate stream key.');
    } finally {
      setIsRegenerating(false);
    }
  };

  const copyToClipboard = async (text, fieldName) => {
    try {
      await Clipboard.setStringAsync(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      showError('Error', 'Failed to copy to clipboard');
    }
  };

  const maskStreamKey = (key) => {
    if (!key || key.length < 16) return '••••••••••••••••';
    // Composite format (2026-07-09): ch_<public>?key=sk_live_<secret> — the
    // channel part is public (it's in every playback URL); mask the secret.
    const qIdx = key.indexOf('?key=');
    if (qIdx !== -1) {
      return `${key.slice(0, qIdx)}?key=sk_live_••••••••${key.slice(-4)}`;
    }
    return 'sk_live_••••••••••••' + key.substring(key.length - 4);
  };

  // Health line (W3): the server's line in a browser; in the app, a live
  // canvas issue points at the Broadcast panel instead of OBS's settings.
  const healthLine = hubHealthLine(health, inDesktopApp);

  if (isLoading) {
    return (
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionIcon}>🎮</Text>
          <Text style={styles.sectionTitle}>CritCash Stream Key</Text>
        </View>
        <View style={styles.loadingBox}>
          <ActivityIndicator color="#FCD000" />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionIcon}>🎮</Text>
        <Text style={styles.sectionTitle}>CritCash Stream Key</Text>
        <View style={styles.requiredBadge}>
          <Text style={styles.requiredText}>REQUIRED</Text>
        </View>
      </View>
      
      <Text style={styles.sectionDescription}>
        {inDesktopApp
          ? HUB_DESKTOP_DESCRIPTION
          : "Download CritCast for Windows (it sets up OBS for you), or stream directly using OBS, Streamlabs, or any RTMP software. One key works for every game you play, and it's also how you take part in events."}
      </Text>

      {keyData?.hasKey ? (
        <View style={styles.keyBox}>
          {/* Status Badge */}
          <View style={styles.statusRow}>
            <View style={[
              styles.statusBadge, 
              { backgroundColor: keyData.status === 'live' ? '#FCD00020' : '#2ECC7120' }
            ]}>
              <Text style={[
                styles.statusText, 
                { color: keyData.status === 'live' ? '#FCD000' : '#2ECC71' }
              ]}>
                {keyData.status === 'live' ? '● LIVE' : '● Ready'}
              </Text>
            </View>
          </View>

          {/* Stream health — only when we actually read a live ingest. Offline
              members and unreadable probes render nothing on purpose. */}
          {healthLine ? (
            <View style={[
              styles.healthBox,
              {
                borderLeftColor: streamHealthTone(health.issues),
                backgroundColor: `${streamHealthTone(health.issues)}10`,
              },
            ]}>
              <Text style={[styles.healthText, { color: streamHealthTone(health.issues) }]}>
                {health.issues?.length ? '⚠️ ' : '● '}{healthLine}
              </Text>
            </View>
          ) : null}

          {/* W3 Clip that (S5.8 item 3): while live, in the browser and in the
              desktop app alike; hidden for the rest of the page session once
              this page learns clipMyStream is not deployed (S8.1). Never in
              ?demo=1, which makes no callable calls. */}
          {clipGateOpen ? (
            <HubClipButton navigation={navigation} onMissing={markClipMissing} />
          ) : null}

          {/* W3 desktop mode: CritCast writes the key into OBS, so the app
              shows one line instead of the Server URL and Stream Key. */}
          {hideKeyBlock(inDesktopApp) ? (
            <View style={styles.desktopKeyNote}>
              <Text style={styles.desktopKeyNoteText}>{HUB_DESKTOP_KEY_LINE}</Text>
            </View>
          ) : (
            <>
              {/* Server URL */}
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Server URL</Text>
                <TouchableOpacity
                  style={styles.fieldValueContainer}
                  onPress={() => copyToClipboard(keyData.rtmpUrl, 'rtmp')}
                >
                  <Text style={styles.fieldValue} numberOfLines={1}>{keyData.rtmpUrl}</Text>
                  <Text style={styles.copyIcon}>{copiedField === 'rtmp' ? '✓' : '📋'}</Text>
                </TouchableOpacity>
              </View>

              {/* Stream Key */}
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Stream Key</Text>
                <TouchableOpacity
                  style={styles.fieldValueContainer}
                  onPress={() => copyToClipboard(keyData.streamKey, 'key')}
                >
                  <Text style={styles.fieldValue}>
                    {showFullKey ? keyData.streamKey : maskStreamKey(keyData.streamKey)}
                  </Text>
                  <Text style={styles.copyIcon}>{copiedField === 'key' ? '✓' : '📋'}</Text>
                </TouchableOpacity>
              </View>
            </>
          )}

          {/* Toggle & Actions (W3: Show is part of the key block; Regenerate
              stays in the desktop app, reached through the panel's Streaming
              Hub link, S3.10) */}
          <View style={styles.actionRow}>
            {!hideKeyBlock(inDesktopApp) ? (
              <TouchableOpacity
                style={styles.toggleButton}
                onPress={() => setShowFullKey(!showFullKey)}
              >
                <Text style={styles.toggleText}>
                  {showFullKey ? '🙈 Hide' : '👁 Show'}
                </Text>
              </TouchableOpacity>
            ) : null}

            <TouchableOpacity 
              style={styles.regenerateButton}
              onPress={handleRegenerateKey}
              disabled={isRegenerating}
            >
              {isRegenerating ? (
                <ActivityIndicator size="small" color="#E67E22" />
              ) : (
                <Text style={styles.regenerateText}>🔄 Regenerate</Text>
              )}
            </TouchableOpacity>
          </View>

          {/* Warning (W3: part of the key block, so not in the desktop app,
              where the key is never shown; inner lines left as they were) */}
          {!hideKeyBlock(inDesktopApp) ? (
          <View style={styles.warningBox}>
            <Text style={styles.warningText}>
              ⚠️ Keep your stream key private! Anyone with this key can stream to your account.
            </Text>
          </View>
          ) : null}
        </View>
      ) : (
        <View style={styles.emptyBox}>
          <Text style={styles.emptyIcon}>🎥</Text>
          <Text style={styles.emptyTitle}>No Stream Key Yet</Text>
          <Text style={styles.emptyText}>
            Download CritCast for Windows to set up OBS and your stream key automatically, or generate a stream key below to broadcast with your own software.
          </Text>
          
          <TouchableOpacity 
            style={styles.generateButton}
            onPress={handleGenerateKey}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <ActivityIndicator color="#000" />
            ) : (
              <Text style={styles.generateButtonText}>Generate Stream Key</Text>
            )}
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};


// ============================================================================
// SIMULCAST SECTION — paid-member restream fan-out (2026-08-03)
// Rebroadcasts the member's CritCash stream to other platforms via the VM
// fleet (stream copied as-is, no transcoding). Dark-launched: backend reports
// productEnabled from the world-readable settings/restream flag. Platform
// stream keys are write-only — saved to fleet Secret Manager, never returned;
// Firestore restreamConfigs holds metadata + keyLast4 only. Live per-
// destination status streams from restreamStatus/{uid} (owner-readable).
// ============================================================================
const SIMULCAST_PLATFORMS = [
  { id: 'twitch', label: 'Twitch', needsServerUrl: false },
  { id: 'youtube', label: 'YouTube', needsServerUrl: false },
  { id: 'kick', label: 'Kick', needsServerUrl: true },
  { id: 'rumble', label: 'Rumble', needsServerUrl: true },
  { id: 'trovo', label: 'Trovo', needsServerUrl: false },
  { id: 'x', label: 'X (Twitter)', needsServerUrl: true },
];
// No vertical-first platforms (Instagram/TikTok) by design — see the note in
// functions/modules/restream.js PLATFORM_DEFS. A 9:16 canvas breaks the
// horizontal read that decides match results.

// Platform-specific gotchas shown under the key field.
const SIMULCAST_PLATFORM_HINTS = {
  rumble: 'Find the Stream URL and Stream Key under Encoder Details in Rumble Studio. Turn on Rumble\'s static (permanent) key so you don\'t have to paste a new one every stream. Rumble caps out at 8 Mbps, and new Rumble accounts have to be verified before they can go live.',
  kick: 'Find the Stream URL and Stream Key under Settings > Stream URL & Key in Kick\'s creator dashboard. Paste the Stream URL exactly as Kick shows it.',
};

const simulcastPlatformLabel = (id) =>
  SIMULCAST_PLATFORMS.find((p) => p.id === id)?.label || id;

const SimulcastSection = ({ showError, navigation }) => {
  const [setup, setSetup] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState('twitch');
  const [streamKey, setStreamKey] = useState('');
  const [serverUrl, setServerUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const functions = getFunctions();
  const demo = isDemoMode();

  const fetchSetup = async () => {
    if (demo) {
      setSetup({
        productEnabled: true,
        isAdmin: false,
        membership: { tier: 'pro', interval: 'year', paid: true },
        maxDestinations: 4,
        enabled: true,
        adminDisabled: false,
        settlePending: false,
        settleReadyAtMs: null,
        destinations: [
          { id: 'dst_demo1', platform: 'twitch', host: 'live.twitch.tv', keyLast4: '4k2p', addedAt: null },
          { id: 'dst_demo2', platform: 'youtube', host: 'a.rtmp.youtube.com', keyLast4: '9x1m', addedAt: null },
        ],
      });
      setStatus({
        sessionState: 'live',
        dest: {
          dst_demo1: { state: 'connected' },
          dst_demo2: { state: 'retrying' },
        },
      });
      setLoading(false);
      return;
    }
    try {
      const fn = httpsCallable(functions, 'getMyRestreamSetup');
      const result = await fn({});
      setSetup(result.data || null);
    } catch (error) {
      // Silent like the stream-key fetch above: the product is dark-launched,
      // so a missing/erroring callable must not toast every Streaming Hub
      // visitor. Mutations below DO surface their errors.
      console.error('Error fetching simulcast setup:', error);
      setSetup(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSetup();
  }, []);

  const paidVisible =
    !!setup && (setup.productEnabled || setup.isAdmin) && !!setup.membership?.paid;

  // Live per-destination status. Missing doc = idle.
  useEffect(() => {
    if (demo || !paidVisible) return;
    const user = getAuth().currentUser;
    if (!user) return;
    const unsub = onSnapshot(
      doc(db, 'restreamStatus', user.uid),
      (snap) => setStatus(snap.exists() ? snap.data() : null),
      () => setStatus(null)
    );
    return () => unsub();
  }, [paidVisible]);

  const badgeForDest = (destId) => {
    const sessionState = status?.sessionState || 'idle';
    if (sessionState === 'queued') return { color: '#FCD000', label: 'Queued' };
    if (sessionState === 'paused_budget') return { color: '#FCD000', label: 'Paused' };
    const state = status?.dest?.[destId]?.state;
    if (state === 'connected') return { color: '#2ECC71', label: 'Live' };
    if (state === 'retrying') return { color: '#FCD000', label: 'Reconnecting' };
    if (state === 'failed') return { color: '#E74C3C', label: 'Check key' };
    return { color: '#666', label: 'Idle' };
  };

  const handleSave = async () => {
    const platform = SIMULCAST_PLATFORMS.find((p) => p.id === selectedPlatform);
    if (!platform) return;
    if (demo) {
      setStreamKey('');
      setServerUrl('');
      showError('Saved', 'Destination added — it goes live with your next stream.');
      return;
    }
    setSaving(true);
    try {
      const fn = httpsCallable(functions, 'saveRestreamDestination');
      const payload = { platform: platform.id, streamKey: streamKey.trim() };
      if (platform.needsServerUrl) payload.serverUrl = serverUrl.trim();
      const result = await fn(payload);
      const destination = result.data?.destination;
      if (destination) {
        setSetup((prev) => prev && ({
          ...prev,
          destinations: [...(prev.destinations || []), destination],
        }));
      }
      setStreamKey('');
      setServerUrl('');
      showError('Saved', 'Destination added — it goes live with your next stream.');
      fetchSetup();
    } catch (error) {
      showError('Error', error.message || 'Could not save that destination.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (dest) => {
    const label = simulcastPlatformLabel(dest.platform);
    const confirmRemove = Platform.OS === 'web'
      ? window.confirm(`Remove ${label} destination?\n\nSimulcast to this platform stops with your next stream. Add it back anytime with a fresh stream key.`)
      : await new Promise((resolve) => {
          Alert.alert(
            `Remove ${label} destination?`,
            'Simulcast to this platform stops with your next stream. Add it back anytime with a fresh stream key.',
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
              { text: 'Remove', style: 'destructive', onPress: () => resolve(true) },
            ]
          );
        });
    if (!confirmRemove) return;

    if (demo) {
      setSetup((prev) => prev && ({
        ...prev,
        destinations: (prev.destinations || []).filter((d) => d.id !== dest.id),
      }));
      return;
    }
    setRemovingId(dest.id);
    try {
      const fn = httpsCallable(functions, 'removeRestreamDestination');
      await fn({ destId: dest.id });
      setSetup((prev) => prev && ({
        ...prev,
        destinations: (prev.destinations || []).filter((d) => d.id !== dest.id),
      }));
      fetchSetup();
    } catch (error) {
      showError('Error', error.message || 'Could not remove that destination.');
    } finally {
      setRemovingId(null);
    }
  };

  const handleToggle = async () => {
    if (demo) {
      setSetup((prev) => prev && ({ ...prev, enabled: !prev.enabled }));
      return;
    }
    setToggling(true);
    try {
      const fn = httpsCallable(functions, 'setRestreamEnabled');
      const result = await fn({ enabled: !setup.enabled });
      setSetup((prev) => prev && ({ ...prev, enabled: !!result.data?.enabled }));
      fetchSetup();
    } catch (error) {
      showError('Error', error.message || 'Could not update Simulcast.');
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    // Bare spinner, no section chrome — most users resolve to "render
    // nothing" while the product is dark, so don't flash a titled card.
    return (
      <View style={[styles.loadingBox, { marginBottom: 16 }]}>
        <ActivityIndicator color="#FCD000" />
      </View>
    );
  }

  // Dark launch: nothing unless the product flag is on (admins always see it).
  if (!setup || (!setup.productEnabled && !setup.isAdmin)) return null;

  // Free tier: locked teaser.
  if (!setup.membership?.paid) {
    return (
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionIcon}>📡</Text>
          <Text style={styles.sectionTitle}>Simulcast</Text>
        </View>
        <Text style={styles.sectionDescription}>
          Stream once, go live everywhere. Paid plans rebroadcast your CritCash stream to Twitch, YouTube, Kick and more — no extra upload, no watermark.
        </Text>
        <TouchableOpacity
          style={styles.simulcastUpgradeBtn}
          onPress={() => navigation.navigate('Subscription')}
        >
          <Text style={styles.simulcastUpgradeBtnText}>Upgrade to unlock</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const destinations = Array.isArray(setup.destinations) ? setup.destinations : [];
  const maxDestinations = Number(setup.maxDestinations) || 0;
  const selected = SIMULCAST_PLATFORMS.find((p) => p.id === selectedPlatform) || SIMULCAST_PLATFORMS[0];
  const saveDisabled = saving || !streamKey.trim() || (selected.needsServerUrl && !serverUrl.trim());
  const anyFailed = !!status?.dest &&
    Object.values(status.dest).some((d) => d?.state === 'failed');

  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionIcon}>📡</Text>
        <Text style={styles.sectionTitle}>Simulcast</Text>
      </View>
      <Text style={styles.sectionDescription}>
        Rebroadcast your CritCash stream to other platforms automatically whenever you go live. Your stream key for each platform stays encrypted — we never display it again after you save it.
      </Text>

      <View style={styles.keyBox}>
        {/* Allowance */}
        <View style={styles.simulcastAllowanceRow}>
          <View style={styles.simulcastAllowanceChip}>
            <Text style={styles.simulcastAllowanceText}>
              {destinations.length} of {maxDestinations} destinations
            </Text>
          </View>
          {setup.membership?.interval === 'month' ? (
            <Text style={styles.simulcastAllowanceHint}>
              Yearly plans get double destinations.
            </Text>
          ) : null}
        </View>

        {/* Destination rows */}
        {destinations.map((dest) => {
          const badge = badgeForDest(dest.id);
          return (
            <View style={styles.simulcastDestRow} key={dest.id}>
              <View style={styles.simulcastDestInfo}>
                <View style={styles.simulcastDestTopRow}>
                  <Text style={styles.simulcastDestPlatform}>
                    {simulcastPlatformLabel(dest.platform)}
                  </Text>
                  <View style={[styles.simulcastBadge, { backgroundColor: `${badge.color}20` }]}>
                    <Text style={[styles.simulcastBadgeText, { color: badge.color }]}>
                      ● {badge.label}
                    </Text>
                  </View>
                </View>
                <Text style={styles.simulcastDestHost} numberOfLines={1}>{dest.host}</Text>
                <Text style={styles.simulcastDestKey}>••••{dest.keyLast4 || ''}</Text>
              </View>
              <TouchableOpacity
                style={styles.simulcastRemoveBtn}
                onPress={() => handleRemove(dest)}
                disabled={removingId === dest.id}
              >
                {removingId === dest.id ? (
                  <ActivityIndicator size="small" color="#E74C3C" />
                ) : (
                  <Text style={styles.simulcastRemoveText}>✕</Text>
                )}
              </TouchableOpacity>
            </View>
          );
        })}

        {anyFailed ? (
          <Text style={styles.simulcastFailHint}>
            A destination has a bad or expired stream key. Paste a fresh key to resume.
          </Text>
        ) : null}

        {/* Add destination */}
        {destinations.length < maxDestinations ? (
          <View style={styles.simulcastAddBox}>
            <Text style={styles.fieldLabel}>Add a destination</Text>
            <View style={styles.simulcastChipRow}>
              {SIMULCAST_PLATFORMS.map((p) => (
                <TouchableOpacity
                  key={p.id}
                  style={[styles.simulcastChip, selectedPlatform === p.id && styles.simulcastChipActive]}
                  onPress={() => setSelectedPlatform(p.id)}
                >
                  <Text style={[styles.simulcastChipText, selectedPlatform === p.id && styles.simulcastChipTextActive]}>
                    {p.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            {selected.needsServerUrl ? (
              <View>
                <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Server URL</Text>
                <TextInput
                  style={styles.infoInput}
                  value={serverUrl}
                  onChangeText={setServerUrl}
                  placeholder="rtmp:// or rtmps:// server URL"
                  placeholderTextColor="#666"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>
            ) : null}

            <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Stream key</Text>
            <TextInput
              style={styles.infoInput}
              value={streamKey}
              onChangeText={setStreamKey}
              placeholder={`Paste your ${selected.label} stream key`}
              placeholderTextColor="#666"
              secureTextEntry
              autoCapitalize="none"
              autoCorrect={false}
            />
            {SIMULCAST_PLATFORM_HINTS[selected.id] ? (
              <Text style={styles.simulcastServerHint}>
                {SIMULCAST_PLATFORM_HINTS[selected.id]}
              </Text>
            ) : null}

            <TouchableOpacity
              style={[styles.simulcastSaveBtn, saveDisabled && { opacity: 0.5 }]}
              onPress={handleSave}
              disabled={saveDisabled}
            >
              {saving ? (
                <ActivityIndicator size="small" color="#000" />
              ) : (
                <Text style={styles.simulcastSaveBtnText}>Save</Text>
              )}
            </TouchableOpacity>
          </View>
        ) : null}

        {/* Enable toggle / gate notices */}
        {setup.adminDisabled ? (
          <View style={styles.simulcastNotice}>
            <Text style={styles.simulcastNoticeText}>
              Simulcast is currently unavailable for your account. Contact support.
            </Text>
          </View>
        ) : setup.settlePending ? (
          <View style={styles.simulcastNotice}>
            <Text style={styles.simulcastNoticeText}>
              Simulcast unlocks about 48 hours after your first plan payment.
              {setup.settleReadyAtMs ? ` Ready around ${new Date(setup.settleReadyAtMs).toLocaleString()}.` : ''}
            </Text>
          </View>
        ) : (
          <View style={styles.simulcastToggleRow}>
            <Text style={styles.simulcastToggleLabel}>Simulcast enabled</Text>
            <TouchableOpacity
              style={[styles.simulcastTogglePill, setup.enabled && styles.simulcastTogglePillOn]}
              onPress={handleToggle}
              disabled={toggling}
            >
              {toggling ? (
                <ActivityIndicator size="small" color={setup.enabled ? '#000' : '#FCD000'} />
              ) : (
                <Text style={[styles.simulcastTogglePillText, setup.enabled && styles.simulcastTogglePillTextOn]}>
                  {setup.enabled ? 'On' : 'Off'}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        )}

        {/* Bitrate guidance — fan-out copies the source stream untouched */}
        <Text style={styles.simulcastFootnote}>
          Simulcast sends your exact stream to every destination with no re-encoding, so keep your OBS canvas horizontal (16:9). On supported games, a vertical canvas breaks the match read we use to confirm event results. Keep your bitrate within your lowest destination's limit too: Twitch is the tightest at about 6-8 Mbps, with Kick and Rumble at 8 Mbps. A 4K/high-bitrate source should only target YouTube.
        </Text>
      </View>
    </View>
  );
};


// ============================================================================
// YOUR STREAM SECTION — title / game / thumbnail (Streams directory, 2026-07-09)
// What viewers see on the /streams browse screen and your watch page. Saved
// via setMyStreamInfo; thumbnail uploads through uploadStreamThumbnail
// (2MB cap, 1280×720 recommended — YouTube model).
// ============================================================================
const StreamInfoSection = ({ showError, navigation }) => {
  const [info, setInfo] = useState({ title: '', game: '', gameCustom: '', details: '', thumbnailUrl: null, schedule: null, nextStreamAtMs: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Channel memberships entry (evergreen 3.5a): getMyStreamInfo.membershipEntry
  // {show, available}. show stays true for a creator who ever offered, whatever
  // the flag, so a creator with renewing members is never locked out. An older
  // server omits it = no row.
  const [membershipEntryShown, setMembershipEntryShown] = useState(false);
  // Team memberships entry (evergreen 3.5b): getMyStreamInfo.teamMembershipEntry
  // {teamId, isCaptain, show, ...}, the ONE source for this row and YourTeam's.
  // null off a team; an older server omits it = no row.
  const [teamMembershipEntry, setTeamMembershipEntry] = useState(null);
  const functions = getFunctions();

  useEffect(() => {
    (async () => {
      try {
        const fn = httpsCallable(functions, 'getMyStreamInfo');
        const result = await fn();
        setMembershipEntryShown(result.data?.membershipEntry?.show === true);
        const teamEntry = result.data?.teamMembershipEntry;
        setTeamMembershipEntry(teamEntry && typeof teamEntry === 'object' ? teamEntry : null);
        setInfo({
          title: result.data?.title || '',
          game: result.data?.game || '',
          gameCustom: result.data?.gameCustom || '',
          details: result.data?.details || '',
          thumbnailUrl: result.data?.thumbnailUrl || null,
          schedule: result.data?.schedule || null,
          nextStreamAtMs: result.data?.nextStreamAtMs || null,
        });
      } catch (_) { /* defaults */ } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    try {
      setSaving(true);
      const fn = httpsCallable(functions, 'setMyStreamInfo');
      await fn({ title: info.title, game: info.game || null, gameCustom: info.gameCustom || null, details: info.details || null });
      setDirty(false);
      showError('Saved', 'Your stream info is updated.');
    } catch (err) {
      showError('Error', err.message || 'Could not save stream info.');
    } finally {
      setSaving(false);
    }
  };

  const pickThumbnail = async () => {
    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) {
      showError('Permission required', 'Allow photo access to upload a thumbnail.');
      return;
    }
    const picked = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [16, 9],
      quality: 0.6,
      base64: true,
    });
    if (picked.canceled) return;

    try {
      setUploadingThumb(true);
      const asset = picked.assets[0];
      let imageBase64 = asset.base64;
      if (!imageBase64) {
        const blob = await (await fetch(asset.uri)).blob();
        imageBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }
      const fn = httpsCallable(functions, 'uploadStreamThumbnail');
      const { data } = await fn({ imageBase64, contentType: 'image/jpeg' });
      setInfo((prev) => ({ ...prev, thumbnailUrl: data?.url || prev.thumbnailUrl }));
      showError('Saved', 'Thumbnail updated.');
    } catch (err) {
      showError('Thumbnail upload failed', err.message || 'Keep it under 2MB (1280×720 recommended).');
    } finally {
      setUploadingThumb(false);
    }
  };

  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionIcon}>📺</Text>
        <Text style={styles.sectionTitle}>Your Stream</Text>
      </View>
      <Text style={styles.sectionDescription}>
        What viewers see on the Streams page — set your title, game, and a promo thumbnail.
      </Text>

      {membershipEntryShown ? (
        <TouchableOpacity
          style={styles.membershipRow}
          onPress={() => navigation.navigate('CreatorMemberships')}
          accessibilityRole="button"
        >
          <Ionicons name="star" size={16} color="#FCD000" style={{ marginRight: 10 }} />
          <Text style={styles.membershipRowText}>Channel memberships</Text>
          <Ionicons name="chevron-forward" size={16} color="#666" />
        </TouchableOpacity>
      ) : null}

      {teamMembershipEntry?.show === true ? (
        teamMembershipEntry.isCaptain === true ? (
          <TouchableOpacity
            style={styles.membershipRow}
            onPress={() => navigation.navigate('CreatorTeamMemberships', { teamId: teamMembershipEntry.teamId })}
            accessibilityRole="button"
            accessibilityLabel="Team memberships"
          >
            <Ionicons name="star" size={16} color="#FCD000" style={{ marginRight: 10 }} />
            <Text style={styles.membershipRowText}>Team memberships</Text>
            <Ionicons name="chevron-forward" size={16} color="#666" />
          </TouchableOpacity>
        ) : (
          <Text style={styles.teamMembershipLine}>Your captain manages team memberships.</Text>
        )
      ) : null}

      {loading ? (
        <View style={styles.loadingBox}><ActivityIndicator color="#FCD000" /></View>
      ) : (
        <View style={styles.keyBox}>
          <Text style={styles.fieldLabel}>Stream title</Text>
          <TextInput
            style={styles.infoInput}
            value={info.title}
            onChangeText={(t) => { setInfo((p) => ({ ...p, title: t })); setDirty(true); }}
            placeholder="What are you playing today?"
            placeholderTextColor="#666"
            maxLength={120}
          />

          <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Details</Text>
          <TextInput
            style={[styles.infoInput, styles.infoDetailsInput]}
            value={info.details}
            onChangeText={(t) => { setInfo((p) => ({ ...p, details: t })); setDirty(true); }}
            placeholder="Tell viewers about your stream — schedule, what you're grinding, your socials…"
            placeholderTextColor="#666"
            multiline
            maxLength={500}
          />
          <Text style={styles.infoCharCount}>{(info.details || '').length}/500</Text>

          <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Game</Text>
          <GameTilePicker
            game={info.game}
            gameCustom={info.gameCustom}
            onChange={({ game, gameCustom }) => { setInfo((p) => ({ ...p, game, gameCustom })); setDirty(true); }}
          />

          <Text style={[styles.fieldLabel, { marginTop: 12 }]}>Thumbnail</Text>
          <TouchableOpacity style={styles.infoThumbPicker} onPress={pickThumbnail} disabled={uploadingThumb}>
            {info.thumbnailUrl ? (
              <Image source={{ uri: info.thumbnailUrl }} style={styles.infoThumbImage} resizeMode="cover" />
            ) : null}
            <Text style={styles.infoThumbHint}>
              {uploadingThumb ? 'Uploading…' : info.thumbnailUrl ? 'Tap to replace' : 'Tap to upload — 1280×720 recommended, 2MB max'}
            </Text>
          </TouchableOpacity>

          <StreamScheduleEditor
            schedule={info.schedule}
            nextStreamAtMs={info.nextStreamAtMs}
            showError={showError}
            onSaved={(d) => setInfo((p) => ({ ...p, schedule: d?.schedule || null, nextStreamAtMs: d?.nextStreamAtMs || null }))}
          />

          <View style={styles.infoActionRow}>
            <TouchableOpacity
              style={[styles.infoSaveBtn, (!dirty || saving) && { opacity: 0.5 }]}
              onPress={save}
              disabled={!dirty || saving}
            >
              {saving ? <ActivityIndicator size="small" color="#000" /> : <Text style={styles.infoSaveBtnText}>Save</Text>}
            </TouchableOpacity>
            <TouchableOpacity style={styles.infoViewLink} onPress={() => navigation.navigate('Streams')}>
              <Text style={styles.infoViewLinkText}>View the Streams page →</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
};

// ============================================================================
// MAIN SCREEN
// ============================================================================
export default function StreamingAccountsScreen({ navigation }) {
  const { showError } = useError();
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const isFocused = useIsFocused();
  // Team Streams v1 (flag-gated; admins exempt). The flag doc is world-
  // readable, so the hook can return true even with no signed-in user —
  // the demo-mode guard on the section below keeps the ?demo=1 screenshot
  // surface unchanged (demo has no auth, and the panel needs callables).
  const squadStreamsVisible = useTeamStreamsVisible();
  // Channel memberships live (evergreen 3.5a): static copy only (the verify
  // banner's availability claim). Dark = today's text, byte-identical.
  const { live: membershipsLive } = useMembershipsLive();

  const fetchUserData = async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        // Demo mode has no signed-in user — don't leave the screen stuck on
        // the loading spinner.
        if (isDemoMode()) setIsLoading(false);
        return;
      }

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        setUserData(userDoc.data());
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isFocused) {
      fetchUserData();
    }
  }, [isFocused]);

  if (isLoading) {
    return (
      <View style={styles.container}>
        <Header navigation={navigation} />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#FCD000" />
          <Text style={styles.loadingText}>Loading streaming settings...</Text>
        </View>
        <NavBar navigation={navigation} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Header navigation={navigation} />
      
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Back Button */}
        <TouchableOpacity 
          style={styles.backButton} 
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}
        >
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Streaming Hub</Text>
          <Text style={styles.headerSubtitle}>
            Configure your stream settings and go live
          </Text>
        </View>

        {/* Earn prompt (evergreen 3.2): Super Crits open on a channel only
            once its owner passes the ID + age check. Computed from the users
            doc this screen already loads on focus, so it clears when they come
            back verified. Renders NOTHING while userData is null (signed out,
            or the read failed) and in ?demo=1, which keeps it out of the
            screenshot surface. Same look as the watch page's owner prompt. */}
        {!isDemoMode() && userData && !isAgeVerifiedIdentity(userData) ? (
          <TouchableOpacity
            style={styles.verifyBanner}
            onPress={() => navigation.navigate('IdVerification')}
            accessibilityRole="button"
          >
            <Ionicons name="id-card-outline" size={15} color="#FCD000" style={{ marginRight: 8 }} />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.verifyBannerStrong}>Verify to start earning</Text>
              <Text style={styles.verifyBannerText}>
                {membershipsLive
                  ? 'Super Crits and Rumbles open on your channel once your ID and age check are done. Then you can offer channel memberships.'
                  : 'Super Crits and Rumbles open on your channel once your ID and age check are done.'}
              </Text>
            </View>
            <View style={styles.verifyBannerBtn}>
              <Text style={styles.verifyBannerBtnText}>Verify</Text>
            </View>
          </TouchableOpacity>
        ) : null}

        {/* CritCash Stream Key Section */}
        <CritCashStreamSection showError={showError} navigation={navigation} />

        {/* Simulcast — paid-member restream fan-out (dark-launched; renders
            nothing until settings/restream.enabled, admins excepted) */}
        <SimulcastSection showError={showError} navigation={navigation} />

        {/* Your Stream — title/game/thumbnail shown on the Streams page */}
        <StreamInfoSection showError={showError} navigation={navigation} />

        {/* Squad Streams — Team Streams v1 manage panel (shared with the
            Streams screen's Your Squads modal). Renders NOTHING when the
            flag is off (non-admin), in demo mode, or signed out — the
            settings/streams flag doc is world-readable, so without the auth
            term a signed-out visitor deep-linking to /streaming would mount
            the panel and its authed callables would error-toast. */}
        {!isDemoMode() && squadStreamsVisible && !!getAuth().currentUser ? (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionIcon}>👥</Text>
              <Text style={styles.sectionTitle}>Squad Streams</Text>
            </View>
            <Text style={styles.sectionDescription}>
              Squad streaming is powered by your Team — stream together and viewers support everyone at once. Manage your roster from the Your Team tab.
            </Text>
            <SquadManagePanel
              navigation={navigation}
              onNavigate={(id) => navigation.navigate('TeamStream', { teamStreamId: id })}
            />
          </View>
        ) : null}

        {/* "Link up?" detection - self-gating (flag + auth + something to
            suggest), shows at most once per session. */}
        {!isDemoMode() ? <SquadLinkPrompt /> : null}

        {/* Help Link */}
        <TouchableOpacity 
          style={styles.helpLink}
          onPress={() => Linking.openURL('https://critcash.com/how-to-play')}
        >
          <Text style={styles.helpLinkText}>Need help setting up? View streaming guide →</Text>
        </TouchableOpacity>

        <View style={{ height: 40 }} />
      </ScrollView>
      
      <NavBar navigation={navigation} />
    </View>
  );
}

// ============================================================================
// STYLES
// ============================================================================
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#888',
    marginTop: 16,
    fontSize: 14,
  },

  // Back Button
  backButton: {
    alignSelf: 'flex-start',
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginBottom: 16,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#333',
    backgroundColor: '#1a1a1a',
  },
  backButtonText: {
    color: '#FCD000',
    fontSize: 15,
    fontWeight: '700',
  },

  // Header
  header: {
    marginBottom: 24,
  },
  headerTitle: {
    color: '#fff',
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  headerSubtitle: {
    color: '#888',
    fontSize: 14,
    lineHeight: 20,
  },

  // "Verify to start earning" banner (mirrors CreatorMetrics' memberBanner
  // and the watch page's owner prompt)
  verifyBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1a1604',
    borderColor: '#4a3d00',
    borderWidth: 1,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 16,
  },
  verifyBannerStrong: { color: '#FCD000', fontSize: 12, fontWeight: '700', lineHeight: 17 },
  verifyBannerText: { color: '#ccc', fontSize: 12, lineHeight: 17 },
  verifyBannerBtn: {
    backgroundColor: '#FCD000',
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 12,
    marginLeft: 10,
  },
  verifyBannerBtnText: { color: '#0b0b0b', fontSize: 12, fontWeight: '800' },

  // Sections
  section: {
    backgroundColor: '#111',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionIcon: {
    fontSize: 24,
    marginRight: 10,
  },
  sectionTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
    flex: 1,
  },
  sectionDescription: {
    color: '#888',
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },
  requiredBadge: {
    backgroundColor: '#FCD00020',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  requiredText: {
    color: '#FCD000',
    fontSize: 10,
    fontWeight: 'bold',
  },

  // Loading Box
  loadingBox: {
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    padding: 40,
    alignItems: 'center',
  },

  // Key Box
  keyBox: {
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    padding: 16,
  },
  statusRow: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  fieldRow: {
    marginBottom: 12,
  },
  fieldLabel: {
    color: '#666',
    fontSize: 12,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  fieldValueContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#222',
    borderRadius: 6,
    padding: 12,
  },
  fieldValue: {
    color: '#fff',
    fontSize: 14,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    flex: 1,
  },
  copyIcon: {
    fontSize: 16,
    marginLeft: 8,
  },
  actionRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
    marginBottom: 16,
  },
  toggleButton: {
    backgroundColor: '#222',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
  },
  toggleText: {
    color: '#fff',
    fontSize: 13,
  },
  regenerateButton: {
    backgroundColor: '#E67E2220',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
  },
  regenerateText: {
    color: '#E67E22',
    fontSize: 13,
  },
  warningBox: {
    backgroundColor: '#FCD00010',
    borderRadius: 6,
    padding: 12,
    borderLeftWidth: 3,
    borderLeftColor: '#FCD000',
  },
  warningText: {
    color: '#FCD000',
    fontSize: 12,
    lineHeight: 18,
  },

  // Stream health line (border/background/text colors are set inline from
  // streamHealthTone so one style serves green, amber and red)
  healthBox: {
    borderRadius: 6,
    padding: 12,
    marginBottom: 16,
    borderLeftWidth: 3,
  },
  healthText: {
    fontSize: 12,
    lineHeight: 18,
  },

  // W3 desktop mode line (replaces the Server URL / Stream Key block in the
  // CritCast app) and the Clip that button under the health line.
  desktopKeyNote: {
    backgroundColor: '#22D3EE10',
    borderRadius: 6,
    padding: 12,
    marginBottom: 8,
    borderLeftWidth: 3,
    borderLeftColor: '#22D3EE',
  },
  desktopKeyNoteText: {
    color: '#22D3EE',
    fontSize: 13,
    lineHeight: 19,
  },
  clipBox: {
    marginBottom: 16,
  },
  clipButton: {
    alignSelf: 'flex-start',
    backgroundColor: '#FCD000',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  clipButtonDisabled: {
    backgroundColor: '#3a3300',
  },
  clipButtonText: {
    color: '#0b0b0b',
    fontSize: 14,
    fontWeight: 'bold',
  },
  clipButtonTextDisabled: {
    color: '#bba64a',
  },
  clipNote: {
    marginTop: 8,
    borderRadius: 6,
    padding: 10,
    borderLeftWidth: 3,
  },
  clipNoteOk: {
    backgroundColor: '#2ECC7110',
    borderLeftColor: '#2ECC71',
  },
  clipNoteRefused: {
    backgroundColor: '#FCD00010',
    borderLeftColor: '#FCD000',
  },
  clipNoteText: {
    fontSize: 12,
    lineHeight: 18,
  },
  clipNoteTextOk: {
    color: '#2ECC71',
  },
  clipNoteTextRefused: {
    color: '#FCD000',
  },
  clipNoteLink: {
    color: '#22D3EE',
    fontSize: 12,
    fontWeight: '700',
    marginTop: 4,
    textDecorationLine: 'underline',
  },

  // Empty Box
  emptyBox: {
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    padding: 24,
    alignItems: 'center',
  },
  emptyIcon: {
    fontSize: 40,
    marginBottom: 12,
  },
  emptyTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  emptyText: {
    color: '#888',
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 20,
  },
  generateButton: {
    backgroundColor: '#FCD000',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  generateButtonText: {
    color: '#000',
    fontSize: 16,
    fontWeight: 'bold',
  },

  // Simulcast (paid-member restream fan-out, 2026-08-03)
  simulcastAllowanceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 14,
  },
  simulcastAllowanceChip: {
    backgroundColor: '#FCD00020',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  simulcastAllowanceText: { color: '#FCD000', fontSize: 12, fontWeight: 'bold' },
  simulcastAllowanceHint: { color: '#888', fontSize: 11, flexShrink: 1 },
  simulcastDestRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#222',
    borderRadius: 6,
    padding: 12,
    marginBottom: 8,
  },
  simulcastDestInfo: { flex: 1 },
  simulcastDestTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  simulcastDestPlatform: { color: '#fff', fontSize: 14, fontWeight: 'bold' },
  simulcastBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
  },
  simulcastBadgeText: { fontSize: 10, fontWeight: 'bold' },
  simulcastDestHost: {
    color: '#888',
    fontSize: 12,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  simulcastDestKey: {
    color: '#666',
    fontSize: 12,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    marginTop: 2,
  },
  simulcastRemoveBtn: {
    backgroundColor: '#E74C3C20',
    borderRadius: 6,
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 10,
  },
  simulcastRemoveText: { color: '#E74C3C', fontSize: 14, fontWeight: 'bold' },
  simulcastFailHint: { color: '#E74C3C', fontSize: 11, lineHeight: 16, marginBottom: 10 },
  simulcastAddBox: { marginTop: 6 },
  simulcastChipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  simulcastChip: {
    backgroundColor: '#161616',
    borderColor: '#2a2a2a',
    borderWidth: 1,
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 14,
  },
  simulcastChipActive: { backgroundColor: '#241d00', borderColor: '#FCD000' },
  simulcastChipText: { color: '#bbb', fontSize: 12 },
  simulcastChipTextActive: { color: '#FCD000', fontWeight: '700' },
  simulcastServerHint: { color: '#666', fontSize: 11, marginTop: 4 },
  simulcastSaveBtn: {
    backgroundColor: '#FCD000',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 24,
    alignItems: 'center',
    alignSelf: 'flex-start',
    marginTop: 14,
  },
  simulcastSaveBtnText: { color: '#000', fontSize: 14, fontWeight: 'bold' },
  simulcastToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 14,
    paddingTop: 14,
    borderTopWidth: 1,
    borderTopColor: '#2a2a2a',
  },
  simulcastToggleLabel: { color: '#fff', fontSize: 14, fontWeight: '600' },
  simulcastTogglePill: {
    backgroundColor: '#222',
    borderColor: '#444',
    borderWidth: 1,
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 18,
    minWidth: 56,
    alignItems: 'center',
  },
  simulcastTogglePillOn: { backgroundColor: '#FCD000', borderColor: '#FCD000' },
  simulcastTogglePillText: { color: '#888', fontSize: 13, fontWeight: 'bold' },
  simulcastTogglePillTextOn: { color: '#000' },
  simulcastNotice: {
    backgroundColor: '#FCD00010',
    borderRadius: 6,
    padding: 12,
    borderLeftWidth: 3,
    borderLeftColor: '#FCD000',
    marginTop: 14,
  },
  simulcastNoticeText: { color: '#FCD000', fontSize: 12, lineHeight: 18 },
  simulcastFootnote: { color: '#888', fontSize: 11, lineHeight: 16, marginTop: 12 },
  simulcastUpgradeBtn: {
    backgroundColor: '#FCD000',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 24,
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  simulcastUpgradeBtnText: { color: '#000', fontSize: 14, fontWeight: 'bold' },

  // Help Link
  helpLink: {
    alignItems: 'center',
    padding: 16,
  },
  helpLinkText: {
    color: '#FCD000',
    fontSize: 14,
  },

  // Your Stream (title/game/thumbnail for the Streams page)
  infoInput: {
    backgroundColor: '#161616',
    borderColor: '#2a2a2a',
    borderWidth: 1,
    borderRadius: 8,
    color: '#EDEDED',
    fontSize: 13,
    paddingVertical: 9,
    paddingHorizontal: 12,
  },
  infoDetailsInput: { minHeight: 84, textAlignVertical: 'top' },
  infoCharCount: { color: '#555', fontSize: 10, textAlign: 'right', marginTop: 3 },
  infoGameChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  infoGameChip: {
    backgroundColor: '#161616',
    borderColor: '#2a2a2a',
    borderWidth: 1,
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 14,
  },
  infoGameChipActive: { backgroundColor: '#241d00', borderColor: '#FCD000' },
  infoGameChipText: { color: '#bbb', fontSize: 12 },
  infoGameChipTextActive: { color: '#FCD000', fontWeight: '700' },
  infoThumbPicker: {
    backgroundColor: '#161616',
    borderColor: '#2a2a2a',
    borderWidth: 1,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    overflow: 'hidden',
  },
  infoThumbImage: { width: '100%', aspectRatio: 16 / 9, borderRadius: 6, marginBottom: 8 },
  infoThumbHint: { color: '#888', fontSize: 11, textAlign: 'center', paddingHorizontal: 10 },
  infoActionRow: { flexDirection: 'row', alignItems: 'center', marginTop: 14, gap: 14 },
  infoSaveBtn: {
    backgroundColor: '#FCD000',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 24,
    alignItems: 'center',
  },
  infoSaveBtnText: { color: '#000', fontSize: 14, fontWeight: 'bold' },
  infoViewLink: { flex: 1 },
  infoViewLinkText: { color: '#FCD000', fontSize: 12 },
  membershipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 14,
    marginBottom: 12,
  },
  membershipRowText: { flex: 1, color: '#FFF', fontSize: 14, fontWeight: '600' },
  teamMembershipLine: { color: '#888', fontSize: 13, lineHeight: 19, marginBottom: 12 },
});